import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, getUsuarioGuardado } from './api';
import { EstadoError } from './EstadoError';

const dinero = (valor) => `$${Number(valor || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fecha = (valor) => {
  if (!valor) return '—';
  const texto = String(valor).slice(0, 10);
  const [a, m, d] = texto.split('-');
  return a && m && d ? `${d}/${m}/${a}` : texto;
};
const boton = (fondo, extra = {}) => ({
  backgroundColor: fondo, color: 'white', border: 'none', borderRadius: 7,
  padding: '9px 13px', fontWeight: 700, cursor: 'pointer', ...extra,
});
const input = { width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 7, border: '1px solid #cbd5e1' };

function claveIdempotencia() {
  if (window.crypto?.randomUUID) return `prov-${window.crypto.randomUUID()}`;
  return `prov-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function Modal({ children, onClose, ancho = 900 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.66)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '28px 12px', overflowY: 'auto' }}>
      <div style={{ width: '100%', maxWidth: ancho, background: 'white', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,.25)', padding: 24, position: 'relative' }}>
        <button aria-label="Cerrar" onClick={onClose} style={{ position: 'absolute', right: 14, top: 12, border: 0, background: '#e2e8f0', borderRadius: 7, padding: '6px 10px', cursor: 'pointer', fontWeight: 700 }}>✕</button>
        {children}
      </div>
    </div>
  );
}

function Tarjeta({ titulo, valor, detalle, color = '#0f172a' }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 11, padding: 15, minWidth: 160, flex: 1 }}>
      <div style={{ color: '#64748b', fontSize: 12, fontWeight: 800 }}>{titulo}</div>
      <div style={{ color, fontSize: 23, fontWeight: 800, marginTop: 4 }}>{valor}</div>
      {detalle && <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>{detalle}</div>}
    </div>
  );
}

export default function Proveedores() {
  const usuario = getUsuarioGuardado();
  const esAdmin = String(usuario?.rol || '').toLowerCase() === 'admin';
  const [proveedores, setProveedores] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [errorCarga, setErrorCarga] = useState('');
  const [cargando, setCargando] = useState(false);

  const [formProveedor, setFormProveedor] = useState(null);
  const [cuenta, setCuenta] = useState(null);
  const [cargandoCuenta, setCargandoCuenta] = useState(false);
  const [movimiento, setMovimiento] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga('');
    try {
      const [lista, totales] = await Promise.all([
        api.get('/api/proveedores'),
        api.get('/api/proveedores/resumen'),
      ]);
      setProveedores(Array.isArray(lista) ? lista : []);
      setResumen(totales || null);
    } catch (err) {
      setErrorCarga(err instanceof ApiError ? err.mensaje : 'No se pudieron cargar los proveedores.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirCuenta = async (proveedor, accionPosterior = null) => {
    setCargandoCuenta(true);
    try {
      const data = await api.get(`/api/proveedores/${proveedor.id}/estado-cuenta`);
      setCuenta(data);
      if (accionPosterior) accionPosterior(data);
    } catch (err) {
      alert(`❌ ${err instanceof ApiError ? err.mensaje : 'No se pudo cargar el estado de cuenta.'}`);
    } finally {
      setCargandoCuenta(false);
    }
  };

  const refrescarCuenta = async () => {
    if (!cuenta?.proveedor?.id) return;
    await abrirCuenta(cuenta.proveedor);
  };

  const proveedoresFiltrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return proveedores.filter((p) => {
      const coincide = !texto || [p.nombre, p.cuit, p.telefono, p.email].some((v) => String(v || '').toLowerCase().includes(texto));
      if (!coincide) return false;
      if (filtro === 'con-deuda') return Number(p.saldo_cc || 0) > 0.009;
      if (filtro === 'vencidos') return Number(p.saldo_vencido || 0) > 0.009;
      return true;
    });
  }, [proveedores, busqueda, filtro]);

  const guardarProveedor = async () => {
    if (!formProveedor?.nombre?.trim()) return alert('El nombre es obligatorio.');
    setGuardando(true);
    try {
      const payload = {
        nombre: formProveedor.nombre,
        cuit: formProveedor.cuit,
        telefono: formProveedor.telefono,
        direccion: formProveedor.direccion,
        email: formProveedor.email,
        dias_pago_default: Number(formProveedor.dias_pago_default || 0),
        observaciones: formProveedor.observaciones,
        activo: formProveedor.activo !== false,
      };
      if (formProveedor.id) await api.put(`/api/proveedores/${formProveedor.id}`, payload);
      else await api.post('/api/proveedores', payload);
      setFormProveedor(null);
      await cargar();
    } catch (err) {
      alert(`❌ ${err instanceof ApiError ? err.mensaje : 'No se pudo guardar el proveedor.'}`);
    } finally {
      setGuardando(false);
    }
  };

  const abrirMovimiento = (tipo, dataCuenta = cuenta, facturaId = '') => {
    if (!dataCuenta?.proveedor) return;
    setMovimiento({
      proveedor: dataCuenta.proveedor,
      tipo,
      monto: '',
      descripcion: '',
      metodo_pago: 'Efectivo',
      factura_id: facturaId ? String(facturaId) : '',
      facturas: dataCuenta.facturas.filter((f) => ['Pendiente', 'Pagada Parcialmente'].includes(f.estado)),
    });
  };

  const registrarMovimiento = async () => {
    const monto = Number(movimiento?.monto);
    if (!Number.isFinite(monto) || monto <= 0) return alert('Ingresá un monto válido.');
    if (movimiento.tipo.startsWith('Ajuste') && String(movimiento.descripcion || '').trim().length < 5) {
      return alert('Indicá el motivo del ajuste.');
    }
    setGuardando(true);
    try {
      await api.post(`/api/proveedores/${movimiento.proveedor.id}/movimiento`, {
        tipo: movimiento.tipo,
        monto,
        descripcion: movimiento.descripcion,
        metodo_pago: movimiento.tipo === 'Pago' ? movimiento.metodo_pago : null,
        factura_id: movimiento.factura_id || null,
        idempotency_key: claveIdempotencia(),
      });
      setMovimiento(null);
      await cargar();
      await refrescarCuenta();
    } catch (err) {
      alert(`❌ ${err instanceof ApiError ? err.mensaje : 'No se pudo registrar el movimiento.'}`);
    } finally {
      setGuardando(false);
    }
  };

  const revertir = async (mov) => {
    const motivo = window.prompt(`Motivo para revertir ${mov.tipo} por ${dinero(mov.monto)}:`);
    if (!motivo) return;
    if (!window.confirm('La reversión generará un contramovimiento auditable. ¿Continuar?')) return;
    setGuardando(true);
    try {
      await api.post(`/api/proveedores/${cuenta.proveedor.id}/movimientos/${mov.id}/revertir`, { motivo });
      await cargar();
      await refrescarCuenta();
    } catch (err) {
      alert(`❌ ${err instanceof ApiError ? err.mensaje : 'No se pudo revertir el movimiento.'}`);
    } finally {
      setGuardando(false);
    }
  };

  const descargarPdf = async () => {
    try {
      const blob = await api.download(`/api/proveedores/${cuenta.proveedor.id}/estado-cuenta/pdf`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Estado_Cuenta_${cuenta.proveedor.nombre.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`❌ ${err instanceof ApiError ? err.mensaje : 'No se pudo generar el PDF.'}`);
    }
  };

  if (errorCarga) return <EstadoError mensaje={errorCarga} onReintentar={cargar} />;

  return (
    <div style={{ padding: 20, background: '#f8fafc', minHeight: '82vh', borderRadius: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, color: '#0f172a' }}>🚚 Proveedores y cuentas por pagar</h2>
          <div style={{ color: '#64748b', marginTop: 4 }}>Facturas, vencimientos, pagos imputados y ajustes auditables.</div>
        </div>
        {esAdmin && <button onClick={() => setFormProveedor({ nombre: '', cuit: '', telefono: '', direccion: '', email: '', dias_pago_default: 30, observaciones: '', activo: true })} style={boton('#7c3aed')}>➕ Nuevo proveedor</button>}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tarjeta titulo="DEUDA TOTAL" valor={dinero(resumen?.deuda_total)} color="#dc2626" />
        <Tarjeta titulo="DEUDA VENCIDA" valor={dinero(resumen?.deuda_vencida)} detalle={`${resumen?.facturas_vencidas || 0} facturas`} color="#b91c1c" />
        <Tarjeta titulo="VENCE EN 7 DÍAS" valor={dinero(resumen?.vence_en_7_dias)} color="#d97706" />
        <Tarjeta titulo="PROVEEDORES ACTIVOS" valor={resumen?.proveedores_activos || 0} color="#2563eb" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 1fr) 190px', gap: 10, marginBottom: 14 }}>
        <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="🔍 Buscar por nombre, CUIT, teléfono o correo" style={input} />
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} style={input}>
          <option value="todos">Todos</option>
          <option value="con-deuda">Con deuda</option>
          <option value="vencidos">Con deuda vencida</option>
        </select>
      </div>

      <div style={{ overflowX: 'auto', background: 'white', border: '1px solid #e2e8f0', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
          <thead style={{ background: '#f1f5f9' }}><tr>
            {['Proveedor', 'Contacto', 'Saldo', 'Vencido', 'Facturas', 'Próximo venc.', 'Acciones'].map((h) => <th key={h} style={{ padding: 12, textAlign: h === 'Saldo' || h === 'Vencido' ? 'right' : 'left', fontSize: 12, color: '#475569' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {proveedoresFiltrados.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: 12 }}><strong>{p.nombre}</strong><div style={{ color: '#64748b', fontSize: 12 }}>{p.cuit || 'Sin CUIT'}</div></td>
                <td style={{ padding: 12, fontSize: 13 }}>{p.telefono || '—'}<div style={{ color: '#64748b' }}>{p.email || ''}</div></td>
                <td style={{ padding: 12, textAlign: 'right', fontWeight: 800, color: Number(p.saldo_cc) > 0 ? '#dc2626' : '#16a34a' }}>{dinero(p.saldo_cc)}</td>
                <td style={{ padding: 12, textAlign: 'right', fontWeight: 800, color: Number(p.saldo_vencido) > 0 ? '#b91c1c' : '#64748b' }}>{dinero(p.saldo_vencido)}</td>
                <td style={{ padding: 12 }}>{p.cantidad_facturas_pendientes || 0}{Number(p.cantidad_facturas_vencidas) > 0 && <span style={{ marginLeft: 6, color: '#b91c1c', fontWeight: 800 }}>({p.cantidad_facturas_vencidas} venc.)</span>}</td>
                <td style={{ padding: 12 }}>{fecha(p.proximo_vencimiento)}</td>
                <td style={{ padding: 12 }}><div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => abrirCuenta(p)} disabled={cargandoCuenta} style={boton('#2563eb', { padding: '7px 10px' })}>📋 Cuenta</button>
                  {esAdmin && <button onClick={() => abrirCuenta(p, (d) => abrirMovimiento('Pago', d))} style={boton('#059669', { padding: '7px 10px' })}>💸 Pagar</button>}
                  {esAdmin && <button onClick={() => setFormProveedor({ ...p, dias_pago_default: p.dias_pago_default ?? 30 })} style={boton('#d97706', { padding: '7px 10px' })}>✏️</button>}
                </div></td>
              </tr>
            ))}
            {!proveedoresFiltrados.length && <tr><td colSpan="7" style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>{cargando ? 'Cargando…' : 'No hay proveedores para mostrar.'}</td></tr>}
          </tbody>
        </table>
      </div>

      {formProveedor && <Modal onClose={() => setFormProveedor(null)} ancho={650}>
        <h3 style={{ marginTop: 0 }}>{formProveedor.id ? 'Editar proveedor' : 'Nuevo proveedor'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label>Nombre *<input style={input} value={formProveedor.nombre || ''} onChange={(e) => setFormProveedor({ ...formProveedor, nombre: e.target.value })} /></label>
          <label>CUIT<input style={input} value={formProveedor.cuit || ''} onChange={(e) => setFormProveedor({ ...formProveedor, cuit: e.target.value })} /></label>
          <label>Teléfono<input style={input} value={formProveedor.telefono || ''} onChange={(e) => setFormProveedor({ ...formProveedor, telefono: e.target.value })} /></label>
          <label>Correo<input type="email" style={input} value={formProveedor.email || ''} onChange={(e) => setFormProveedor({ ...formProveedor, email: e.target.value })} /></label>
          <label style={{ gridColumn: '1 / -1' }}>Dirección<input style={input} value={formProveedor.direccion || ''} onChange={(e) => setFormProveedor({ ...formProveedor, direccion: e.target.value })} /></label>
          <label>Días de pago habituales<input type="number" min="0" max="3650" style={input} value={formProveedor.dias_pago_default ?? 30} onChange={(e) => setFormProveedor({ ...formProveedor, dias_pago_default: e.target.value })} /></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 22 }}><input type="checkbox" checked={formProveedor.activo !== false} onChange={(e) => setFormProveedor({ ...formProveedor, activo: e.target.checked })} /> Proveedor activo</label>
          <label style={{ gridColumn: '1 / -1' }}>Observaciones<textarea rows="3" style={input} value={formProveedor.observaciones || ''} onChange={(e) => setFormProveedor({ ...formProveedor, observaciones: e.target.value })} /></label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}><button onClick={() => setFormProveedor(null)} style={boton('#64748b')}>Cancelar</button><button disabled={guardando} onClick={guardarProveedor} style={boton('#7c3aed')}>{guardando ? 'Guardando…' : 'Guardar'}</button></div>
      </Modal>}

      {cuenta && <Modal onClose={() => setCuenta(null)} ancho={1120}>
        <div style={{ paddingRight: 38 }}>
          <h2 style={{ margin: 0 }}>Estado de cuenta — {cuenta.proveedor.nombre}</h2>
          <div style={{ color: '#64748b', marginTop: 4 }}>{cuenta.proveedor.cuit || 'Sin CUIT'} • {cuenta.proveedor.telefono || 'Sin teléfono'}</div>
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', margin: '15px 0' }}>
          <button onClick={descargarPdf} style={boton('#475569')}>📄 Descargar PDF</button>
          {esAdmin && <button onClick={() => abrirMovimiento('Pago')} style={boton('#059669')}>💸 Registrar pago</button>}
          {esAdmin && <button onClick={() => abrirMovimiento('Ajuste Débito')} style={boton('#dc2626')}>➕ Ajuste débito</button>}
          {esAdmin && <button onClick={() => abrirMovimiento('Ajuste Crédito')} style={boton('#2563eb')}>➖ Ajuste crédito</button>}
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 15 }}>
          <Tarjeta titulo="SALDO TOTAL" valor={dinero(cuenta.resumen.saldo_registrado)} color="#dc2626" />
          <Tarjeta titulo="EN FACTURAS" valor={dinero(cuenta.resumen.saldo_facturas)} />
          <Tarjeta titulo="VENCIDO" valor={dinero(cuenta.resumen.saldo_vencido)} color="#b91c1c" />
          <Tarjeta titulo="SALDO MANUAL" valor={dinero(cuenta.resumen.saldo_manual)} detalle="Ajustes/deudas sin factura" color="#7c3aed" />
        </div>

        <h3>Antigüedad de deuda</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(110px,1fr))', gap: 7, marginBottom: 18 }}>
          {[
            ['Sin venc.', 'sin_vencimiento'], ['A vencer', 'no_vencido'], ['1–30 días', 'de_1_a_30'],
            ['31–60 días', 'de_31_a_60'], ['61–90 días', 'de_61_a_90'], ['+90 días', 'mas_de_90'],
          ].map(([t, k]) => <div key={k} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}><div style={{ color: '#64748b', fontSize: 11 }}>{t}</div><strong>{dinero(cuenta.resumen.antiguedad[k])}</strong></div>)}
        </div>

        <h3>Facturas</h3>
        <div style={{ overflowX: 'auto', maxHeight: 260, border: '1px solid #e2e8f0', borderRadius: 8 }}><table style={{ width: '100%', minWidth: 820, borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}><tr>{['Emisión', 'Comprobante', 'Vencimiento', 'Total', 'Saldo', 'Estado', ''].map((h) => <th key={h} style={{ padding: 9, textAlign: 'left', fontSize: 12 }}>{h}</th>)}</tr></thead>
          <tbody>{cuenta.facturas.map((f) => <tr key={f.id} style={{ borderTop: '1px solid #e2e8f0', background: f.vencida ? '#fff7f7' : 'white' }}>
            <td style={{ padding: 9 }}>{fecha(f.fecha_emision)}</td><td style={{ padding: 9 }}>{f.tipo_comprobante} {f.numero_comprobante || `#${f.id}`}</td><td style={{ padding: 9, color: f.vencida ? '#b91c1c' : undefined }}>{fecha(f.fecha_vencimiento)}{f.vencida ? ` (${f.dias_mora} d)` : ''}</td>
            <td style={{ padding: 9 }}>{dinero(f.importe_total)}</td><td style={{ padding: 9, fontWeight: 800 }}>{dinero(f.saldo_pendiente)}</td><td style={{ padding: 9 }}>{f.estado}</td>
            <td style={{ padding: 9 }}>{esAdmin && ['Pendiente', 'Pagada Parcialmente'].includes(f.estado) && <button onClick={() => abrirMovimiento('Pago', cuenta, f.id)} style={boton('#059669', { padding: '5px 8px', fontSize: 12 })}>Pagar</button>}</td>
          </tr>)}</tbody>
        </table></div>

        <h3 style={{ marginTop: 20 }}>Movimientos</h3>
        <div style={{ overflowX: 'auto', maxHeight: 300, border: '1px solid #e2e8f0', borderRadius: 8 }}><table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}><tr>{['Fecha', 'Tipo', 'Descripción', 'Medio', 'Importe', 'Saldo', 'Usuario', ''].map((h) => <th key={h} style={{ padding: 9, textAlign: 'left', fontSize: 12 }}>{h}</th>)}</tr></thead>
          <tbody>{cuenta.movimientos.map((m) => {
            const reduce = ['Pago', 'Ajuste Crédito'].includes(m.tipo);
            const reversible = esAdmin && !m.revertido && !m.movimiento_origen_id && ['Pago', 'Deuda', 'Ajuste Débito', 'Ajuste Crédito'].includes(m.tipo);
            return <tr key={m.id} style={{ borderTop: '1px solid #e2e8f0', opacity: m.revertido ? .55 : 1 }}>
              <td style={{ padding: 9 }}>{new Date(m.fecha).toLocaleString('es-AR')}</td><td style={{ padding: 9, fontWeight: 700 }}>{m.tipo}{m.revertido ? ' (revertido)' : ''}</td><td style={{ padding: 9 }}>{m.descripcion || '—'}</td><td style={{ padding: 9 }}>{m.metodo_pago || '—'}</td>
              <td style={{ padding: 9, fontWeight: 800, color: reduce ? '#059669' : '#dc2626' }}>{reduce ? '-' : '+'}{dinero(m.monto)}</td><td style={{ padding: 9 }}>{m.saldo_posterior == null ? '—' : dinero(m.saldo_posterior)}</td><td style={{ padding: 9 }}>{m.cargado_por || '—'}</td>
              <td style={{ padding: 9 }}>{reversible && <button disabled={guardando} onClick={() => revertir(m)} style={boton('#b91c1c', { padding: '5px 8px', fontSize: 11 })}>Revertir</button>}</td>
            </tr>;
          })}</tbody>
        </table></div>
      </Modal>}

      {movimiento && <Modal onClose={() => setMovimiento(null)} ancho={570}>
        <h3 style={{ marginTop: 0 }}>{movimiento.tipo} — {movimiento.proveedor.nombre}</h3>
        <div style={{ background: '#f8fafc', padding: 11, borderRadius: 8, marginBottom: 12 }}>Saldo actual: <strong>{dinero(cuenta?.resumen?.saldo_registrado ?? movimiento.proveedor.saldo_cc)}</strong></div>
        <label>Importe *<input autoFocus type="number" min="0.01" step="0.01" style={input} value={movimiento.monto} onChange={(e) => setMovimiento({ ...movimiento, monto: e.target.value })} /></label>
        {movimiento.tipo === 'Pago' && <>
          <label style={{ display: 'block', marginTop: 12 }}>Imputación<select style={input} value={movimiento.factura_id} onChange={(e) => {
            const id = e.target.value; const factura = movimiento.facturas.find((f) => String(f.id) === id);
            setMovimiento({ ...movimiento, factura_id: id, monto: factura ? String(factura.saldo_pendiente) : movimiento.monto });
          }}><option value="">Automática: facturas más antiguas primero</option>{movimiento.facturas.map((f) => <option key={f.id} value={f.id}>{f.tipo_comprobante} {f.numero_comprobante || `#${f.id}`} — saldo {dinero(f.saldo_pendiente)} — vence {fecha(f.fecha_vencimiento)}</option>)}</select></label>
          <label style={{ display: 'block', marginTop: 12 }}>Medio de pago<select style={input} value={movimiento.metodo_pago} onChange={(e) => setMovimiento({ ...movimiento, metodo_pago: e.target.value })}>{['Efectivo', 'Transferencia', 'Mercado Pago', 'Tarjeta', 'Cheque'].map((m) => <option key={m}>{m}</option>)}</select></label>
          {movimiento.metodo_pago === 'Efectivo' && <div style={{ marginTop: 8, color: '#b45309', fontSize: 12 }}>El pago generará un egreso en la caja abierta.</div>}
        </>}
        <label style={{ display: 'block', marginTop: 12 }}>{movimiento.tipo === 'Pago' ? 'Observación' : 'Motivo *'}<textarea rows="3" style={input} value={movimiento.descripcion} onChange={(e) => setMovimiento({ ...movimiento, descripcion: e.target.value })} /></label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}><button onClick={() => setMovimiento(null)} style={boton('#64748b')}>Cancelar</button><button disabled={guardando} onClick={registrarMovimiento} style={boton(movimiento.tipo === 'Pago' ? '#059669' : movimiento.tipo === 'Ajuste Crédito' ? '#2563eb' : '#dc2626')}>{guardando ? 'Registrando…' : 'Confirmar'}</button></div>
      </Modal>}
    </div>
  );
}
